import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Library, Lock, Crown, ChevronRight, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { CASE_STUDIES } from "@/data/staticData";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const riskColors = { high: "text-amber-700 bg-amber-50 border-amber-200", limited: "text-blue-700 bg-blue-50 border-blue-200", minimal: "text-green-700 bg-green-50 border-green-200" };
const riskLabels = { high: "Eleve", limited: "Limite", minimal: "Minimal" };

export default function CaseStudiesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const isPremium = user?.role === "premium" || user?.role === "admin";
  const sectors = ["all", ...new Set(CASE_STUDIES.map(c => c.sector))];

  const filtered = filter === "all" ? CASE_STUDIES : CASE_STUDIES.filter(c => c.sector === filter);
  const visibleStudies = isPremium ? filtered : filtered.slice(0, 1);
  const lockedCount = isPremium ? 0 : Math.max(0, filtered.length - 2);

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="case-studies-page">
      <div className="mb-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <Library className="h-6 w-6 text-[var(--ia-navy)]" />
          <h1 className="text-2xl font-bold text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Etudes de cas</h1>
        </div>
        <p className="text-[var(--ia-text-muted)]">Analyses détaillées de cas réels d'utilisation de l'IA</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {sectors.map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`cs-filter-${s}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-all ${filter === s ? "bg-[var(--ia-navy)] text-white" : "bg-white border border-[var(--ia-border)] text-slate-600 hover:bg-slate-50"}`}>
            {s === "all" ? "Tous" : s}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {visibleStudies.map((cs, i) => (
          <Card key={cs.id} className="border-[var(--ia-border)] animate-fade-in" style={{ animationDelay: `${i * 80}ms` }} data-testid={`case-study-${cs.id}`}>
            <Accordion type="single" collapsible>
              <AccordionItem value={cs.id} className="border-none">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="text-left">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={`text-[10px] ${riskColors[cs.risk]}`}>{riskLabels[cs.risk]}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">{cs.sector}</Badge>
                    </div>
                    <h3 className="font-semibold text-sm">{cs.title}</h3>
                    <p className="text-xs text-[var(--ia-text-muted)] mt-0.5">{cs.description}</p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                    <div>
                      <h4 className="text-xs font-semibold text-[var(--ia-blue)] mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Réglementations</h4>
                      <ul className="space-y-1">
                        {cs.regulations.map((r, j) => (
                          <li key={j} className="text-xs text-slate-600 flex items-start gap-1.5">
                            <ChevronRight className="h-3 w-3 text-blue-400 mt-0.5 flex-shrink-0" />{r}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-red-600 mb-2 flex items-center gap-1"><XCircle className="h-3 w-3" /> Pièges à éviter</h4>
                      <ul className="space-y-1">
                        {cs.pitfalls.map((p, j) => (
                          <li key={j} className="text-xs text-slate-600 flex items-start gap-1.5">
                            <ChevronRight className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />{p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-green-600 mb-2 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Actions</h4>
                      <ul className="space-y-1">
                        {cs.actions.map((a, j) => (
                          <li key={j} className="text-xs text-slate-600 flex items-start gap-1.5">
                            <ChevronRight className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />{a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
        ))}

        {lockedCount > 0 && (
          <Card className="border-[var(--ia-border)] border-dashed bg-slate-50/50 animate-fade-in" data-testid="case-studies-locked">
            <CardContent className="p-8 text-center">
              <Lock className="h-8 w-8 text-[var(--ia-muted)] mx-auto mb-3" />
              <p className="text-sm text-[var(--ia-text-muted)] mb-3">{lockedCount} étude(s) de cas supplémentaire(s) disponible(s) en Premium</p>
              <Button onClick={() => navigate("/dashboard/pricing")} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="cs-upgrade-btn">
                <Crown className="h-4 w-4 mr-2" /> Passer en Premium
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
